import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DotsHorizontalIcon } from 'src/assets/tailwind/Notifications/dots-horizontal.svg';
import { ReactComponent as NotificationIcon } from 'src/assets/tailwind/Notifications/notification-icon.svg';
import { Button } from 'src/components/ui/button';
import { convertDate } from 'src/utils/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'src/components/ui/dropdown-menu';
import { Icon } from 'src/components/ui/icon';
import { Typography } from 'src/components/ui/typography';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { cn } from 'src/utils/utils';
import { categoryIconMap, categoryKeyMap, subCategoryDescriptionKeyMap, subCategoryKeyMap, subCategorySummaryKeyMap } from '../constants';
import { getTranslatedText } from '../utils';

import type { NotificationRowProps } from '../types';

const NotificationRow = ({
  notification,
  onDismiss,
  onMarkReadStatus
}: NotificationRowProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  const translatedDescription = getTranslatedText(subCategoryDescriptionKeyMap, notification?.sub_category_name, notification?.description_vars, t);

  useEffect(() => {
    const el = textRef.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight);
  }, [translatedDescription]);

  return (
    <div className={cn('tw:border-b tw:border-border-secondary')}>
      <div className={cn('tw:flex tw:py-m tw:px-s tw:gap-m')}>
        <div className="tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center">
          <Icon
            icon={categoryIconMap[notification?.category_name] ?? NotificationIcon}
            sizeClass="tw:h-l tw:w-l"
            colorClass="tw:text-icon-primary"
          />
        </div>
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-3">
          <div className="tw:min-w-0">
            <Typography
              variant="h5"
              colorClass="tw:text-text-primary"
              align="left"
              className="tw:line-clamp-1"
            >
              {notification?.sub_category_name ? t(subCategoryKeyMap[notification.sub_category_name] ?? notification.sub_category_name): ''}
            </Typography>
            <Typography
              ref={textRef}
              variant="b3"
              colorClass="tw:text-text-secondary"
              align="left"
              className={expanded ? undefined : 'tw:line-clamp-1'}
            >
              {translatedDescription}
            </Typography>
          </div>
          <Typography
            variant="b3"
            colorClass="tw:text-text-secondary"
            align="left"
          >
            {notification?.category_name ? t(categoryKeyMap[notification.category_name] ?? notification.category_name) : ''} · {t(T.source)} · {convertDate(new Date(notification?.created_on ?? ''), t)} {t(T.ago)}
          </Typography>
        </div>

        <span
          className={cn(
            'tw:mt-1 tw:h-s tw:w-s tw:shrink-0 tw:rounded-full tw:bg-brand',
            notification?.is_read && 'tw:invisible'
          )}
          aria-hidden
        />
      </div>

      <div className="tw:flex tw:gap-m tw:pb-s tw:pl-12">
        {isClamped && (
          <Button
            hierarchy="link"
            size="small"
            text={expanded ? t(T.viewLess) : t(T.viewMore)}
            onClick={() => setExpanded(prev => !prev)}
          />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              iconOnly
              hierarchy="ghost"
              size="small"
              leftIcon={DotsHorizontalIcon}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="tw:w-60">
            <DropdownMenuItem
              className="tw:flex tw:flex-col tw:items-start tw:gap-s tw:p-2 tw:flex-1"
              onClick={() =>
                onMarkReadStatus?.(notification.id, !notification.is_read)
              }
            >
              {notification.is_read ? t(T.markAsUnread) : t(T.markAsRead)}
            </DropdownMenuItem>
            {/* <DropdownMenuItem className="tw:flex tw:flex-col tw:items-start tw:gap-s tw:p-2 tw:flex-1">
              {t(T.mute)}
            </DropdownMenuItem> */}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="tw:flex tw:flex-col tw:items-start tw:gap-s tw:p-2 tw:flex-1 tw:text-text-error"
              onClick={() => onDismiss?.(notification?.id)}
            >
              {t(T.dismiss)}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default NotificationRow;
